export interface RuntimeConfig {
  apiUrl: string;
  kratosUrl: string;
  smartCaptchaSiteKey: string;
}

// Дефолтные значения для локальной разработки
const DEFAULT_CONFIG: RuntimeConfig = {
  apiUrl: '/api',
  kratosUrl: '/kratos',
  smartCaptchaSiteKey: '',
};

// Хранилище для загруженного конфига
let runtimeConfig: RuntimeConfig | null = null;
// Промис для предотвращения гонки запросов
let configLoadPromise: Promise<RuntimeConfig> | null = null;

/**
 * Загружает runtime конфигурацию из config.json
 * Этот файл подменяется бэкендом при запуске контейнера
 */
export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (runtimeConfig) {
    return runtimeConfig;
  }

  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = (async () => {
    try {
      const response = await fetch('/config.json', {
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store', // Не кэшируем, чтобы всегда получать актуальный конфиг
      });

      if (!response.ok) {
        console.warn('Failed to load config.json, using defaults');
        runtimeConfig = DEFAULT_CONFIG;
        return runtimeConfig;
      }

      const config = await response.json();

      // Проверяем, что конфиг не содержит плейсхолдеров (для локальной разработки)
      runtimeConfig = {
        apiUrl: getConfigValue(config.apiUrl, DEFAULT_CONFIG.apiUrl),
        kratosUrl: getConfigValue(config.kratosUrl, DEFAULT_CONFIG.kratosUrl),
        smartCaptchaSiteKey: getConfigValue(config.smartCaptchaSiteKey, DEFAULT_CONFIG.smartCaptchaSiteKey),
      };

      console.log('Runtime config loaded:', runtimeConfig);
      return runtimeConfig;
    } catch (error) {
      console.error('Error loading runtime config:', error);
      runtimeConfig = DEFAULT_CONFIG;
      return runtimeConfig;
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
};

/**
 * Получает загруженную конфигурацию
 * Если конфиг еще не загружен, возвращает дефолтные значения
 */
export const getRuntimeConfig = (): RuntimeConfig => {
  return runtimeConfig || DEFAULT_CONFIG;
};

const getConfigValue = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string' || value.includes('%%') || value === '') {
    return fallback;
  }

  return value;
};

export const __resetRuntimeConfigForTests = () => {
  runtimeConfig = null;
  configLoadPromise = null;
};
